import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { CheckCircle2, Clock, Loader2 } from "lucide-react";
import { z } from "zod";
import { getOrder } from "@/lib/board.functions";
import { formatIDR } from "@/lib/board-constants";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";

export const Route = createFileRoute("/status")({
  validateSearch: z.object({ order: z.string().optional() }),
  head: () => ({
    meta: [
      { title: "Status Pembayaran — jasasebar.com" },
      { name: "description", content: "Pantau status pembayaran dan pembaruan peringkat listing Anda." },
      { property: "og:title", content: "Status Pembayaran — jasasebar.com" },
      { property: "og:description", content: "Pantau status pembayaran dan pembaruan peringkat listing Anda." },
    ],
  }),
  component: StatusPage,
});

function StatusPage() {
  const { order } = Route.useSearch();
  const orderFn = useServerFn(getOrder);

  const { data, isLoading } = useQuery({
    queryKey: ["order", order],
    queryFn: () => orderFn({ data: { merchantOrderId: order! } }),
    enabled: !!order,
    refetchInterval: 5000,
  });

  const paid = data?.status === "PAID";

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-4 py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="surface-card p-8 text-center"
        >
          {!order && <p className="text-sm text-muted-foreground">Tidak ada nomor pesanan pada tautan ini.</p>}
          {order && isLoading && (
            <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Memeriksa pembayaran…
            </p>
          )}
          {data && (
            <>
              <span
                className={`mx-auto grid size-14 place-items-center rounded-2xl ${
                  paid ? "bg-success/15 text-success" : "bg-warning/15 text-warning"
                }`}
              >
                {paid ? <CheckCircle2 className="size-7" /> : <Clock className="size-7" />}
              </span>
              <h1 className="mt-5 font-display text-2xl font-semibold">
                {paid ? "Pembayaran berhasil" : "Menunggu pembayaran"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {data.name} · {formatIDR(data.amount)} · #{data.merchant_order_id}
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {paid
                  ? "Peringkat listing Anda sudah diperbarui di papan."
                  : "Halaman ini menyegar otomatis setiap 5 detik setelah pembayaran dikonfirmasi sistem pembayaran."}
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Link
                  to="/produk/$slug"
                  params={{ slug: data.slug }}
                  className="h-11 rounded-xl border border-border px-5 text-sm leading-11 font-medium"
                >
                  Lihat detail
                </Link>
                <Link
                  to="/"
                  className="h-11 rounded-xl bg-primary px-5 text-sm leading-11 font-medium text-primary-foreground"
                >
                  Kembali ke papan
                </Link>
              </div>
            </>
          )}
        </motion.div>
      </main>
      <SiteFooter />
    </div>
  );
}
